import { useRef, useEffect, useMemo, useState } from 'react';
import pLimit from 'p-limit';
import { isDevEnvironment, useOnce } from '../../../../utils';

const CONCURRENT_DOWNLOADS_LIMIT = 10;

export function usePreloadingImageSequence({ keyFrames, imageUrls = [] }) {
  const limit = useRef(pLimit(CONCURRENT_DOWNLOADS_LIMIT));
  let [cache] = useState(() => Array(imageUrls.length));

  let frames = useMemo(() => generateSequenceArray(imageUrls, keyFrames), [imageUrls, keyFrames]);

  useOnce(() => () => limit.current.clearQueue()); // cancel all downloads instantly on unmount

  if (!imageUrls || imageUrls.length < 1) {
    throw new Error('Urls or length not set for PreloadingImageSequence');
  }

  useEffect(() => {
    if (!frames || frames.length === 0) return;

    let sortedFrames = [...frames].sort(sortImageSequence);
    let loadUrl = (url, frameNumber) => loadAndPopulateCache(url, frameNumber, sortedFrames, cache);
    let c = sortedFrames.map(frame => limit.current(() => loadUrl(frame.url, frame.frameNumber)));

    if (isDevEnvironment) {
      Promise.all(c).then(() => void console.log('ALL IMAGES LOADED:', sortedFrames));
    }
  }, [cache, frames]);

  return { frames, cache };
}

export function sortImageSequence(a, b) {
  if (a.priority === b.priority) {
    return a.frameNumber - b.frameNumber;
  }
  return a.priority < b.priority ? 1 : -1;
}

export function generateSequenceArrayFromTo(from, to) {
  const length = to - from;
  return Array.apply(null, Array(length)).map((_, index) => {
    return { frameNumber: index += from };
  });
}

export function generateSequenceArray(imageUrls, keyFrames) {
  if (!keyFrames.length) return;
  let from = 0;
  let to = imageUrls.length;
  let getPriority = keyframe => (keyframe !== -1 ? 10 : 0);
  let mapFrameNumber = ({ frameNumber }) => ({
    frameNumber,
    url: imageUrls[frameNumber],
    priority: getPriority(keyFrames.indexOf(frameNumber)),
  });

  return generateSequenceArrayFromTo(from, to).map(mapFrameNumber);
}

function loadAndPopulateCache(url, frameNumber, frames, cache) {
  return load(url, frameNumber)
    .then(result => {
      if (result.err) {
        frames[result.frameNumber].err = result.err;
      }
      frames[result.frameNumber].image = result.image;
      cache[result.frameNumber] = result.image;
      return result;
    })
    .catch(err => {
      console.log('This should never happen as reject is not used', err);
    });
}

function load(url, frameNumber) {
  return new Promise(function(resolve) {
    var request = new XMLHttpRequest();
    request.open('GET', url);
    request.responseType = 'blob';
    request.onload = () => {
      if (request.status === 200) {
        resolve({ frameNumber, image: URL.createObjectURL(request.response) });
      } else {
        resolve({ frameNumber, image: undefined, err: request.statusText }); // fail gracefully and recover later.
      }
    };
    request.onerror = e => {
      resolve({ frameNumber, image: undefined, err: e }); // fail gracefully and recover later.
    };
    request.send();
  });
}
